import { type FormEvent, useState } from 'react'
import { type ApiError, type PrefixInfo, lookupPrefix } from '../lib/api'

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'result'; data: PrefixInfo }
  | { kind: 'error'; error: ApiError }

// Small free-oracle affordance: type a 3-digit AWB prefix, get the issuing airline.
export function PrefixLookup() {
  const [code, setCode] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!code.trim()) return
    setState({ kind: 'loading' })
    const res = await lookupPrefix(code)
    setState(res.ok ? { kind: 'result', data: res.data } : { kind: 'error', error: res.error })
  }

  return (
    <div className="card p-5">
      <div className="mono text-[11px] uppercase tracking-[0.18em] text-signal">Prefix oracle</div>
      <h3 className="mt-1 text-lg font-bold tracking-tight text-ink">Whose AWB prefix?</h3>
      <p className="mt-1 text-sm text-muted">
        The first 3 digits of any AWB identify the issuing airline. Free, offline lookup.
      </p>
      <form onSubmit={onSubmit} className="mt-3 flex items-center gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
          placeholder="176"
          inputMode="numeric"
          className="mono w-24 rounded-xl border border-border-strong bg-surface-0 px-3 py-2 text-center text-lg font-semibold text-ink outline-none focus:border-signal tnum"
        />
        <button
          type="submit"
          disabled={state.kind === 'loading' || code.length < 3}
          className="rounded-xl border border-border-strong bg-surface-1 px-4 py-2 font-semibold text-ink transition-colors hover:border-signal disabled:opacity-45"
        >
          Resolve
        </button>
      </form>

      {state.kind === 'result' && (
        <div className="reveal mt-3 rounded-xl border border-border bg-surface-1/60 p-3">
          <div className="flex items-center gap-2">
            <span className="mono rounded-md bg-signal px-2 py-0.5 text-xs font-bold text-on-accent">
              {state.data.iata}
            </span>
            <span className="font-semibold text-ink">{state.data.name}</span>
          </div>
          <a
            href={state.data.site}
            target="_blank"
            rel="noreferrer"
            className="mono mt-2 block truncate text-xs text-muted hover:text-signal"
          >
            {state.data.site} ↗
          </a>
          {state.data.platform && (
            <div className="mono mt-1 text-[11px] uppercase tracking-wide text-faint">
              platform · {state.data.platform}
            </div>
          )}
        </div>
      )}
      {state.kind === 'error' && (
        <p className="mt-3 text-sm text-muted">
          Unknown prefix. Try 176 (Emirates), 160 (Cathay), or 020 (Lufthansa).
        </p>
      )}
    </div>
  )
}
